import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';
import { fetchGHGInventoryData } from '../components/charts/GHGInventorySupabase';

export interface InventoryComparisonResult {
  chartData: {
    labels: number[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor: string | string[];
      borderColor: string | string[];
      fill?: boolean;
      tension?: number;
      borderWidth?: number;
      pointRadius?: number;
      pointHoverRadius?: number;
    }>;
  };
  chartOptions: any;
  sectorBasedTotal: number;
  consumptionBasedTotal: number;
  difference: number;
  differencePercent: number;
  latestYear: number;
}

export async function fetchInventoryComparisonData(location: string): Promise<InventoryComparisonResult> {
  try {
    console.log('🔍 Fetching inventory comparison data for:', location);
    
    // Get geofips for the location
    const geofips = await supabaseLocationService.getGeofips(location);
    if (!geofips) {
      throw new Error(`Could not find geofips for location: ${location}`);
    }
    
    console.log('🔍 Geofips for location:', geofips);
    
    // Get location info to determine if it's a city or county
    const { data: locationData, error: locationError } = await supabaseClient
      .from('population')
      .select('location_name, geography_type, county')
      .eq('geofips', geofips)
      .limit(1);
    
    if (locationError) {
      console.error('Error fetching location data:', locationError);
      throw locationError;
    }
    
    if (!locationData || locationData.length === 0) {
      throw new Error(`No location data found for geofips: ${geofips}`);
    }
    
    const locationInfo = locationData[0];
    console.log('🔍 Location info:', locationInfo);
    
    // Determine which table to use for consumption-based data
    let tableName: string;
    let queryGeofips = geofips;
    
    if (locationInfo.geography_type === 'City') {
      tableName = 'cbei_cities';
      console.log('🔍 Using cbei_cities table for city');
    } else if (locationInfo.geography_type === 'County') {
      tableName = 'cbei_counties';
      console.log('🔍 Using cbei_counties table for county');
    } else {
      throw new Error(`Unsupported geography type: ${locationInfo.geography_type}`);
    }
    
    // Fetch sector-based GHG inventory data
    console.log('🔍 Fetching sector-based GHG inventory data...');
    const sectorBasedResult = await fetchGHGInventoryData(location);
    
    // Fetch consumption-based data for all available years
    console.log('🔍 Fetching consumption-based data for all years...');
    const { data: consumptionData, error: consumptionError } = await supabaseClient
      .from(tableName)
      .select('year, transportation_total_emissions, housing_total_emissions, food_total_emissions, goods_total_emissions, services_total_emissions, hhcount')
      .eq('geofips', queryGeofips)
      .order('year', { ascending: true });
    
    if (consumptionError) {
      console.error(`Error fetching consumption-based GHG data from ${tableName}:`, consumptionError);
      throw consumptionError;
    }
    
    if (!consumptionData || consumptionData.length === 0) {
      throw new Error(`No consumption-based GHG data found for ${location} in ${tableName}`);
    }
    
    console.log('🔍 Consumption data rows:', consumptionData.length);
    
    // Process consumption-based data for all years
    const consumptionByYear: { [year: number]: number } = {};
    consumptionData.forEach(row => {
      const hhcount = row.hhcount || 1;
      const total = 
        ((row.transportation_total_emissions || 0) / 1000000) * hhcount +
        ((row.housing_total_emissions || 0) / 1000000) * hhcount +
        ((row.food_total_emissions || 0) / 1000000) * hhcount +
        ((row.goods_total_emissions || 0) / 1000000) * hhcount +
        ((row.services_total_emissions || 0) / 1000000) * hhcount;
      
      consumptionByYear[row.year] = total;
    });
    
    // Calculate sector-based totals for all years
    const sectorByYear: { [year: number]: number } = {};
    sectorBasedResult.chartData.labels.forEach((yearLabel: any, index: number) => {
      const year = parseInt(yearLabel.toString());
      let total = 0;
      
      sectorBasedResult.chartData.datasets.forEach((dataset: any) => {
        const value = dataset.data[index] as number;
        if (typeof value === 'number' && !isNaN(value)) {
          total += value;
        }
      });
      
      sectorByYear[year] = total;
    });
    
    // Get all available years (intersection of both datasets)
    const allYears = Array.from(new Set([
      ...Object.keys(sectorByYear).map(Number),
      ...Object.keys(consumptionByYear).map(Number)
    ])).sort((a, b) => a - b);
    
    console.log('🔍 Available years:', allYears);
    
    // Prepare data arrays for chart
    const sectorData: number[] = [];
    const consumptionDataArray: number[] = [];
    const years: number[] = [];
    
    allYears.forEach(year => {
      if (sectorByYear[year] !== undefined && consumptionByYear[year] !== undefined) {
        years.push(year);
        sectorData.push(sectorByYear[year]);
        consumptionDataArray.push(consumptionByYear[year]);
      }
    });
    
    // Calculate latest year metrics for comparison
    const latestYear = Math.max(...years);
    const latestSectorTotal = sectorByYear[latestYear] || 0;
    const latestConsumptionTotal = consumptionByYear[latestYear] || 0;
    const difference = latestConsumptionTotal - latestSectorTotal;
    const differencePercent = latestSectorTotal > 0 ? (difference / latestSectorTotal) * 100 : 0;
    
    console.log('🔍 Latest year metrics:', {
      latestYear,
      latestSectorTotal,
      latestConsumptionTotal,
      difference,
      differencePercent
    });
    
    // Prepare chart data for line chart
    const chartData = {
      labels: years,
      datasets: [
        {
          label: 'Sector-Based Inventory',
          data: sectorData,
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderColor: '#3b82f6',
          borderWidth: 3,
          fill: false,
          tension: 0.2,
          pointRadius: 4,
          pointHoverRadius: 6
        },
        {
          label: 'Consumption-Based Inventory',
          data: consumptionDataArray,
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderColor: '#10b981',
          borderWidth: 3,
          fill: false,
          tension: 0.2,
          pointRadius: 4,
          pointHoverRadius: 6
        }
      ]
    };
    
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 10,
          bottom: 10,
          left: 10,
          right: 10
        }
      },
      plugins: {
        title: {
          display: true,
          text: `Inventory Comparison - ${location}`,
          font: {
            size: 16,
            weight: 'bold' as const
          },
          padding: {
            top: 10,
            bottom: 20
          }
        },
        legend: {
          position: 'bottom' as const,
          labels: {
            usePointStyle: true,
            padding: 15,
            font: {
              size: 13
            }
          }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const value = context.parsed.y;
              const label = context.dataset.label;
              return `${label}: ${value.toLocaleString()} MT CO₂e`;
            }
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Year',
            font: {
              size: 13,
              weight: 'bold' as const
            },
            padding: { top: 10, bottom: 5 }
          },
          ticks: {
            font: {
              size: 12
            },
            maxRotation: 45,
            minRotation: 45
          },
          grid: {
            display: false
          }
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Total Emissions (MT CO₂e)',
            font: {
              size: 13,
              weight: 'bold' as const
            },
            padding: { top: 5, bottom: 10 }
          },
          ticks: {
            font: {
              size: 12
            },
            callback: (value: any) => Number(value).toLocaleString()
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.05)'
          }
        }
      }
    };
    
    const result: InventoryComparisonResult = {
      chartData,
      chartOptions,
      sectorBasedTotal: latestSectorTotal,
      consumptionBasedTotal: latestConsumptionTotal,
      difference,
      differencePercent,
      latestYear
    };
    
    console.log('✅ Inventory comparison data processed successfully:', {
      sectorBasedTotal: latestSectorTotal,
      consumptionBasedTotal: latestConsumptionTotal,
      difference,
      differencePercent,
      latestYear
    });
    
    return result;
    
  } catch (error) {
    console.error('❌ Error fetching inventory comparison data:', error);
    throw error;
  }
}
